import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../config/firebase";
import { toast } from "react-toastify";
import "../css/Auth.css";

const INTER_FONT = "'Inter', Arial, Helvetica, sans-serif";

// Maps Firebase Auth error codes to user-facing messages.
// auth/invalid-credential is the modern code (Firebase 9+ with Email Enumeration Protection).
// auth/wrong-password and auth/user-not-found are kept for SDK compatibility; both map to
// the same generic message so account existence is never confirmed or denied.
function mapSignInError(err) {
  const code = err?.code || "";
  const msg  = err?.message || "";

  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "The email or password is incorrect. Please try again.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/user-disabled":
      return "This account has been disabled. Please contact support.";
    case "auth/too-many-requests":
      return "Too many sign-in attempts. Please wait a few minutes and try again.";
    case "auth/popup-blocked":
      return "Sign-in popup was blocked by your browser. Please allow popups for this site and try again.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return null; // user cancelled — nothing to show
    default:
      if (msg.toLowerCase().includes("network") || msg.toLowerCase().includes("offline")) {
        return "Network error. Please check your connection and try again.";
      }
      return "Sign in failed. Please try again.";
  }
}

export default function SignIn() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const handleChange = (e) => {
    if (error) setError(null);
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { email, password } = formData;
    setError(null);
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success("Logged in!");
      navigate("/");
    } catch (err) {
      console.error("[SignIn] auth error code:", err?.code);
      const friendly = mapSignInError(err);
      if (friendly) setError(friendly);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
      toast.success("Logged in with Google!");
      navigate("/");
    } catch (err) {
      console.error("[SignIn] Google auth error code:", err?.code);
      const friendly = mapSignInError(err);
      if (friendly) setError(friendly);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="auth-container"
      style={{
        fontFamily: INTER_FONT,
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      <video
        className="background-video"
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: -1,
        }}
      >
        <source src="/videos/PavilionVid.mp4" type="video/mp4" />
      </video>

      <form
        className="auth-form"
        onSubmit={handleSubmit}
        style={{ fontFamily: INTER_FONT, zIndex: 1 }}
      >
        <h2 style={{ fontFamily: INTER_FONT, fontWeight: 700 }}>Log In</h2>

        <input
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          required
          autoComplete="email"
          style={{ fontFamily: INTER_FONT }}
        />

        <input
          type="password"
          name="password"
          placeholder="Password"
          value={formData.password}
          onChange={handleChange}
          required
          autoComplete="current-password"
          style={{ fontFamily: INTER_FONT }}
        />

        <div style={{ textAlign: "right", marginTop: 4, marginBottom: -8 }}>
          <Link
            to="/forgot-password"
            style={{
              color: "rgba(255,255,255,0.65)",
              fontSize: "0.82rem",
              textDecoration: "underline",
              fontFamily: INTER_FONT,
            }}
          >
            Forgot password?
          </Link>
        </div>

        {error && (
          <p className="auth-msg auth-msg--error" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className={loading ? "loading" : ""}
          style={{ fontFamily: INTER_FONT, fontWeight: 700 }}
        >
          {loading ? "Logging In..." : "Log In"}
        </button>

        <button
          type="button"
          className={`google-btn ${loading ? "loading" : ""}`}
          onClick={handleGoogleSignIn}
          disabled={loading}
          style={{ fontFamily: INTER_FONT, fontWeight: 700 }}
        >
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt="Google logo"
          />
          {loading ? "Please wait..." : "Sign in with Google"}
        </button>

        <p className="switch-link" style={{ fontFamily: INTER_FONT }}>
          Don't have an account?{" "}
          <span
            onClick={() => navigate("/sign-up")}
            style={{
              cursor: "pointer",
              fontFamily: INTER_FONT,
              textDecoration: "underline",
            }}
          >
            Sign Up
          </span>
        </p>
      </form>
    </div>
  );
}
